import { inject, Injectable } from '@angular/core';

import { Eip1193Provider } from '../models';
import {
  WalletAccountType,
  WalletConnectionError,
  WalletConnectionErrorCode,
  WalletConnectorId,
  WalletIdentity,
  WalletProviderConnector,
} from '../models';
import { REOWN_APPKIT_ACCESS, ReownAppKitModal, ReownAppKitNetworkState } from '../tokens';
import { normalizeEvmAddress } from '../utils';

const EIP155_NAMESPACE = 'eip155';
const CONNECT_TIMEOUT_MS = 120_000;
const RESTORE_TIMEOUT_MS = 5_000;

@Injectable({ providedIn: 'root' })
export class ReownWalletConnector implements WalletProviderConnector {
  readonly #appKit = inject(REOWN_APPKIT_ACCESS);

  readonly #accountHandlers = new Set<(accounts: readonly string[]) => void>();
  readonly #chainHandlers = new Set<(chainIdHex: `0x${string}`) => void>();
  readonly #disconnectHandlers = new Set<() => void>();
  #modalSubscriptions: Array<() => void> = [];

  isAvailable(): boolean {
    return this.#appKit.isConfigured();
  }

  async closeNetworkSelectionModal(): Promise<void> {
    const modal = await this.#appKit.getModal();

    await modal?.close();
  }

  async connect(): Promise<WalletIdentity> {
    const modal = await this.#appKit.requireModal();
    this.#ensureModalSubscriptions(modal);
    const connectedIdentity = this.#readIdentity(modal);

    if (connectedIdentity) {
      return connectedIdentity;
    }

    return this.#openAndWaitForIdentity(modal);
  }

  async disconnect(): Promise<void> {
    const modal = await this.#appKit.getModal();

    if (!modal) {
      return;
    }

    await modal.disconnect(EIP155_NAMESPACE);
  }

  async resetNetworkSelection(): Promise<void> {
    const modal = await this.#appKit.getModal();
    const [defaultNetwork] = modal?.getCaipNetworks(EIP155_NAMESPACE) ?? [];

    if (defaultNetwork) {
      modal?.setCaipNetwork(defaultNetwork);
    }
  }

  async switchToChain(chainId: number): Promise<void> {
    const modal = await this.#appKit.requireModal();
    const targetNetwork = modal.getCaipNetworks(EIP155_NAMESPACE).find(network => Number(network.id) === chainId);

    if (!targetNetwork) {
      throw new WalletConnectionError(WalletConnectionErrorCode.UnsupportedChain, `Unsupported EVM chain requested for switch: ${chainId}`);
    }

    try {
      await modal.switchNetwork(targetNetwork, { throwOnFailure: true });
    } catch (error) {
      throw normalizeSwitchNetworkError(error);
    }
  }

  async getConnectedIdentity(): Promise<WalletIdentity | null> {
    const modal = await this.#appKit.getModal();

    if (!modal) {
      return null;
    }

    this.#ensureModalSubscriptions(modal);
    const identity = this.#readIdentity(modal);

    if (identity || !isRestorePending(modal)) {
      return identity;
    }

    return this.#waitForRestoredIdentity(modal);
  }

  async getEip1193Provider(): Promise<Eip1193Provider> {
    const provider = (await this.#appKit.requireModal()).getWalletProvider();

    if (!isEip1193Provider(provider)) {
      throw new WalletConnectionError(WalletConnectionErrorCode.ProviderUnavailable, 'Connected wallet provider is unavailable.');
    }

    return provider;
  }

  onAccountsChanged(handler: (accounts: readonly string[]) => void): () => void {
    this.#accountHandlers.add(handler);
    this.#ensureCachedModalSubscriptions();

    return () => {
      this.#accountHandlers.delete(handler);
    };
  }

  onChainChanged(handler: (chainIdHex: `0x${string}`) => void): () => void {
    this.#chainHandlers.add(handler);
    this.#ensureCachedModalSubscriptions();

    return () => {
      this.#chainHandlers.delete(handler);
    };
  }

  onDisconnect(handler: () => void): () => void {
    this.#disconnectHandlers.add(handler);
    this.#ensureCachedModalSubscriptions();

    return () => {
      this.#disconnectHandlers.delete(handler);
    };
  }

  #openAndWaitForIdentity(modal: ReownAppKitModal): Promise<WalletIdentity> {
    return new Promise((resolve, reject) => {
      let settled = false;
      const cleanups: Array<() => void> = [];

      const finish = (callback: () => void): void => {
        if (settled) {
          return;
        }

        settled = true;
        cleanups.forEach(cleanup => cleanup());
        callback();
      };

      const tryResolve = (): void => {
        try {
          const identity = this.#readIdentity(modal);

          if (identity) {
            finish(() => resolve(identity));
          }
        } catch (error) {
          finish(() => reject(error));
        }
      };

      const rejectAsClosed = (): void => {
        tryResolve();

        if (!settled) {
          finish(() =>
            reject(
              new WalletConnectionError(
                WalletConnectionErrorCode.UserRejected,
                'Wallet connection was closed before an account was selected.',
              ),
            ),
          );
        }
      };

      const timeoutId = globalThis.setTimeout(() => {
        finish(() => reject(new WalletConnectionError(WalletConnectionErrorCode.UserRejected, 'Wallet connection request timed out.')));
      }, CONNECT_TIMEOUT_MS);

      cleanups.push(
        () => globalThis.clearTimeout(timeoutId),
        modal.subscribeAccount(() => tryResolve(), EIP155_NAMESPACE),
        modal.subscribeNetwork(() => tryResolve()),
        modal.subscribeState(state => {
          if (!state.open) {
            rejectAsClosed();
          }
        }),
      );

      void modal
        .open({ view: 'Connect' })
        .then(tryResolve)
        .catch(error => {
          finish(() => reject(normalizeReownError(error)));
        });
    });
  }

  #waitForRestoredIdentity(modal: ReownAppKitModal): Promise<WalletIdentity | null> {
    return new Promise((resolve, reject) => {
      let settled = false;
      const cleanups: Array<() => void> = [];

      const finish = (callback: () => void): void => {
        if (settled) {
          return;
        }

        settled = true;
        cleanups.forEach(cleanup => cleanup());
        callback();
      };

      const tryResolve = (): void => {
        try {
          const identity = this.#readIdentity(modal);

          if (identity) {
            finish(() => resolve(identity));

            return;
          }

          if (isRestoreSettledWithoutIdentity(modal)) {
            finish(() => resolve(null));
          }
        } catch (error) {
          finish(() => reject(error));
        }
      };

      const timeoutId = globalThis.setTimeout(() => {
        finish(() => resolve(null));
      }, RESTORE_TIMEOUT_MS);

      cleanups.push(
        () => globalThis.clearTimeout(timeoutId),
        modal.subscribeAccount(() => tryResolve(), EIP155_NAMESPACE),
        modal.subscribeNetwork(() => tryResolve()),
      );
      tryResolve();
    });
  }

  #readIdentity(modal: ReownAppKitModal): WalletIdentity | null {
    const address = modal.getAddress(EIP155_NAMESPACE);
    const chain = parseChainId(modal.getChainId());

    if (!modal.getIsConnectedState() || !address || !chain) {
      return null;
    }

    const normalizedAddress = normalizeEvmAddress(address);

    if (!normalizedAddress) {
      throw new WalletConnectionError(WalletConnectionErrorCode.NoAccounts, 'Wallet returned an invalid account address.');
    }

    return {
      accountAddress: normalizedAddress,
      accountType: WalletAccountType.Eoa,
      chainId: chain.chainId,
      chainIdHex: chain.chainIdHex,
      connectorId: WalletConnectorId.Reown,
      ownerAddress: normalizedAddress,
    };
  }

  #ensureCachedModalSubscriptions(): void {
    const modal = this.#appKit.getCachedModal();

    if (modal) {
      this.#ensureModalSubscriptions(modal);
    }
  }

  #ensureModalSubscriptions(modal: ReownAppKitModal): void {
    if (this.#modalSubscriptions.length > 0) {
      return;
    }

    this.#modalSubscriptions = [
      modal.subscribeAccount(state => {
        const accounts = state.isConnected && state.address ? [state.address] : [];
        this.#accountHandlers.forEach(handler => handler(accounts));

        if (!state.isConnected) {
          this.#disconnectHandlers.forEach(handler => handler());
        }
      }, EIP155_NAMESPACE),
      modal.subscribeNetwork(state => {
        const chain = parseChainId(state.chainId);

        if (chain) {
          this.#chainHandlers.forEach(handler => handler(chain.chainIdHex));
        }
      }),
    ];
  }
}

function parseChainId(chainId: ReownAppKitNetworkState['chainId']): { chainId: number; chainIdHex: `0x${string}` } | null {
  if (typeof chainId === 'number') {
    return normalizeNumericChainId(chainId);
  }

  if (typeof chainId !== 'string') {
    return null;
  }

  const parsedChainId = chainId.startsWith('0x') ? Number.parseInt(chainId, 16) : Number.parseInt(chainId, 10);

  return normalizeNumericChainId(parsedChainId);
}

function normalizeNumericChainId(chainId: number): { chainId: number; chainIdHex: `0x${string}` } | null {
  if (!Number.isSafeInteger(chainId) || chainId < 1) {
    throw new WalletConnectionError(WalletConnectionErrorCode.Unknown, 'Wallet did not return a valid chain id.');
  }

  return {
    chainId,
    chainIdHex: `0x${chainId.toString(16)}`,
  };
}

function normalizeReownError(error: unknown): WalletConnectionError {
  if (error instanceof WalletConnectionError) {
    return error;
  }

  if (error instanceof Error) {
    return new WalletConnectionError(WalletConnectionErrorCode.Unknown, error.message);
  }

  return new WalletConnectionError(WalletConnectionErrorCode.Unknown, 'Wallet connection failed.');
}

const USER_REJECTED_ERROR_CODE = 4001;

function normalizeSwitchNetworkError(error: unknown): WalletConnectionError {
  if (error instanceof WalletConnectionError) {
    return error;
  }

  if (isUserRejectedError(error)) {
    return new WalletConnectionError(WalletConnectionErrorCode.UserRejected, 'Wallet network switch was rejected.');
  }

  return normalizeReownError(error);
}

function isUserRejectedError(error: unknown): boolean {
  if (typeof error !== 'object' || error === null) {
    return false;
  }

  const code = (error as { code?: unknown }).code;

  return code === USER_REJECTED_ERROR_CODE || code === 'ACTION_REJECTED';
}

function isEip1193Provider(provider: unknown): provider is Eip1193Provider {
  return typeof provider === 'object' && provider !== null && 'request' in provider && typeof provider.request === 'function';
}

function isRestorePending(modal: ReownAppKitModal): boolean {
  const status = modal.getAccount(EIP155_NAMESPACE)?.status;

  return status === 'connecting' || status === 'reconnecting';
}

function isRestoreSettledWithoutIdentity(modal: ReownAppKitModal): boolean {
  const status = modal.getAccount(EIP155_NAMESPACE)?.status;

  return status === undefined || status === 'disconnected';
}
